import * as Sentry from '@sentry/react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { UserInfo } from 'PublicEntityTypes';
import { createContext, useEffect, useRef } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router';
import useSWR, { KeyedMutator } from 'swr';
import Loading from '../../design-system/loading/loading';
import { useAnalytics } from '../analytics-context/use-analytics';
import { isInternalOrg } from './utils/is-internal-org';

const USER_INFO_PATH = '/auth/user-info';

type UserInfoContextData = UserInfo & {
  mutations: {
    mutateUserInfo: KeyedMutator<UserInfo>;
    logout: () => void;
  };
};

const UserInfoContext = createContext<UserInfoContextData | null>(null);

// TODO: rename to UserProvider after we remove the other UserProvider
export const UserInfoProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: userInfo, isLoading, error, mutate: mutateUserInfo } = useSWR<UserInfo>(USER_INFO_PATH);
  const { showBoundary } = useErrorBoundary();
  const ldClient = useLDClient();
  const analytics = useAnalytics();
  const location = useLocation();

  const userInfoRef = useRef(userInfo);
  const middlewareAdded = useRef(false);
  const lastTrackedPathname = useRef<string | null>(null);

  useEffect(() => {
    if (userInfo?.authenticatedInfo?.id) {
      analytics.identify(userInfo.authenticatedInfo.id, {
        name: userInfo.authenticatedInfo.name,
        email: userInfo.authenticatedInfo.email,
      });

      analytics.group(userInfo.authenticatedInfo.activeOrg.id, {
        name: userInfo.authenticatedInfo.activeOrg.name,
        logoUrl: userInfo.authenticatedInfo.activeOrg.logoUrl,
        isInternalOrg: isInternalOrg(userInfo.authenticatedInfo.email),
      });

      if (lastTrackedPathname.current !== location.pathname) {
        lastTrackedPathname.current = location.pathname;

        analytics.page(location.pathname, {
          title: location.pathname,
        });
      }
    }
  }, [analytics, userInfo?.authenticatedInfo, location.pathname]);

  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  useEffect(() => {
    if (middlewareAdded.current) {
      return;
    }

    middlewareAdded.current = true;

    analytics.addSourceMiddleware(({ payload, next }) => {
      if (payload.obj.type === 'track' || payload.obj.type === 'page') {
        const userInfo = userInfoRef.current?.authenticatedInfo;

        payload.obj.properties = {
          ...payload.obj.properties,
          orgId: userInfo?.activeOrg?.id,
          orgName: userInfo?.activeOrg?.name,
          userEmail: userInfo?.email,
          isInternalOrg: isInternalOrg(userInfo?.email ?? ''),
          groupId: userInfo?.activeOrg?.id,
        };
      }

      next(payload);
    });
  }, [analytics]);

  if (isLoading) {
    return <Loading text="Authenticating..." />;
  }
  if (!userInfo) {
    // Add Sentry breadcrumb before calling showBoundary to track undefined errors
    Sentry.addBreadcrumb({
      category: 'user_info_provider',
      message: 'UserInfoProvider showing error boundary due to missing userInfo',
      level: 'error',
      data: {
        hasError: !!error,
        errorMessage: error?.message || 'undefined',
        errorType: typeof error,
        isLoading,
        userInfoExists: !!userInfo,
      },
    });

    showBoundary(error || new Error('UserInfo is undefined in UserInfoProvider'));

    return null;
  }

  if (userInfo.isAuthenticated) {
    // Save the user's email to localStorage for login hint
    if (userInfo.authenticatedInfo?.email) {
      localStorage.setItem('lastUserEmail', userInfo.authenticatedInfo.email);
    }

    Sentry.setUser({
      id: userInfo.authenticatedInfo?.id,
      email: userInfo.authenticatedInfo?.email,
      activeOrgName: userInfo.authenticatedInfo?.activeOrg?.name,
      activeOrgId: userInfo.authenticatedInfo?.activeOrg?.id,
    });

    ldClient?.identify({
      kind: 'multi',
      user: {
        key: userInfo.authenticatedInfo?.id,
        email: userInfo.authenticatedInfo?.email,
      },
      organization: {
        key: userInfo.authenticatedInfo?.activeOrg?.id,
        name: userInfo.authenticatedInfo?.activeOrg?.name,
      },
    });

    // Handle LaunchDarkly network errors gracefully to prevent Sentry noise
    // Reference: https://support.launchdarkly.com/hc/en-us/articles/12998125691419-Error-LaunchDarklyFlagFetchError-network-error
    ldClient?.on('error', (e) => {
      // Suppress LaunchDarkly network errors - these are transient and the SDK handles them gracefully
      if (e?.message?.includes('LaunchDarklyFlagFetchError: network error')) {
        // This error is expected and handled by the SDK internally
        // The SDK will continue to retry and use cached flag values
        return;
      }
      // Log other LaunchDarkly errors that might be actionable
      Sentry.captureException(e);
    });
  }

  const logout = () => {
    // Save the last user email before clearing localStorage
    const lastUserEmail = localStorage.getItem('lastUserEmail');

    localStorage.clear();
    sessionStorage.clear();

    // Restore the last user email after clearing
    if (lastUserEmail) {
      localStorage.setItem('lastUserEmail', lastUserEmail);
    }

    // https://www.twilio.com/docs/segment/connections/sources/catalog/libraries/website/javascript#reset-or-log-out
    analytics.reset();

    window.location.href = '/auth/logout';
  };

  const contextData = {
    ...userInfo,
    mutations: {
      mutateUserInfo,
      logout,
    },
  };

  return <UserInfoContext.Provider value={contextData}>{children}</UserInfoContext.Provider>;
};

UserInfoProvider.UserInfoContext = UserInfoContext;
